import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { TrendingUp, Shield, Truck, Clock, Package, DollarSign } from "lucide-react";
import RolePreviewLayout from "@/components/welcome/RolePreviewLayout";
import { MultiLayerValue } from "@/components/welcome/MultiLayerValue";
import { analytics, getVariant } from "@/lib/analytics";

const accent = {
  text: "text-blue-400",
  border: "border-blue-400/30",
  bgSoft: "bg-blue-400/5",
  bg: "bg-blue-400",
};

export default function WelcomeBuyer() {
  const [params] = useSearchParams();
  const name = params.get("name") || "there";
  const variant = getVariant("welcome_buyer");
  useEffect(() => { analytics.rolePreviewViewed("buyer", variant); }, [variant]);

  const isB = variant === "b";

  return (
    <>
    <RolePreviewLayout
      role="buyer"
      accent={accent}
      eyebrow={`Application received · Buyer / Importer · variant ${variant.toUpperCase()}`}
      headline={isB
        ? <>Welcome, <span className="text-blue-400">{name}</span>. <br className="hidden sm:block" />Buy with confidence. <span className="text-[#22C55E]">AI-scored</span> on every deal.</>
        : <>Welcome, <span className="text-blue-400">{name}</span>. <br className="hidden sm:block" />Pay smart. Release fast. Improve cash flow.</>}
      subline={isB
        ? "Every exporter on Delta GreenX is KYC-verified, multi-source corporate intelligence rated, and AI-scored on price, risk, and delivery track record. Stop hedging on Excel — execute with confidence."
        : "As a verified buyer on Delta GreenX, you'll execute capital flows with milestone-linked release, get verified counterparties, and cut your working-capital cycle by 20-30 days."}
      primaryStat={isB
        ? { value: "97/100", label: "Avg AI Confidence Score" }
        : { value: "20–30d", label: "Faster cash conversion" }}
      benefits={[
        { icon: DollarSign, title: "Pay only on milestones", desc: "Capital is held until shipment + delivery triggers. Zero up-front lock-in." },
        { icon: Shield, title: "Verified exporters only", desc: "Every counterparty is KYC-verified with multi-source corporate intelligence." },
        { icon: TrendingUp, title: "30–45% working capital efficiency", desc: "Cycle 45-60d → 20-30d. Stock right, sell more, free up capital." },
      ]}
      dashboard={
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ["Active Orders", "3", "text-white"],
              ["Capital Locked", "$295K", "text-blue-400"],
              ["Avg Days to Close", "26d", "text-[#22C55E]"],
              ["Verified Suppliers", "12", "text-purple-400"],
            ].map(([l, v, c]) => (
              <div key={l} className="bg-[#050A07] border border-[#1A2E22] p-3">
                <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">{l}</p>
                <p className={`font-['Outfit'] text-xl font-bold ${c}`}>{v}</p>
              </div>
            ))}
          </div>
          <div className="bg-[#050A07] border border-[#1A2E22] p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white text-sm font-semibold">25 MT Black Tiger Shrimp · Pacific Harvest VN</span>
              <span className="text-yellow-400 text-[10px] border border-yellow-400/30 px-2 py-0.5">Awaiting Capital Lock</span>
            </div>
            <div className="grid grid-cols-5 gap-1 mt-3">
              {["Capital Secured", "Execution", "Shipped", "Delivered", "Released"].map((m, i) => (
                <div key={m} className={`text-[9px] py-1 px-1.5 text-center border ${i === 0 ? "border-yellow-400/40 text-yellow-400" : "border-[#1A2E22] text-[#A1B2A8]/40"}`}>{m}</div>
              ))}
            </div>
          </div>
        </div>
      }
      nextSteps={[
        { title: "KYC verification (1-2 days)", desc: "Bank reference + import license. We pull authoritative public-registry signals where applicable." },
        { title: "First counterparty match", desc: "Our AI Confidence Engine scores exporters on price, risk, and delivery track record." },
        { title: "Execute your first flow", desc: "Bank-grade payment rails secure capital release on milestone triggers — no spreadsheets, no faxes." },
      ]}
    />
    <MultiLayerValue persona="buyer" />
    </>
  );
}
